import {set} from '@vueuse/core';
import {v4 as uuidv4} from 'uuid';
import type {Gsoa_ProductCatalog_ProductDetail as GsoaProductCatalogProductDetail} from '~/openapi/models/Gsoa_ProductCatalog_ProductDetail';
import {GsoaRecommenderService} from '~/openapi/services/GsoaRecommenderService';
import {LuigisBoxService} from '~/openapi/services/LuigisBoxService';
import {StoryblokSearchService} from '~/openapi/services/StoryblokSearchService';
import {cancelTimeout, useCurrentUrl} from '~/utils';

const LUIGIS_BOX_CLIENT_ID_COOKIE = 'luigisBoxClientId';
const LUIGIS_BOX_COOKIE = '_lb_id';

export interface SearchData {
	text: string,
	gsoaId: number,
}

export interface SearchAnalyticsData {
	text: string,
	products: GsoaProductCatalogProductDetail[],
	positionOffset?: number,
}

export interface StoryblokSearchContent {
	body?: string,
	fullSlug: string,
	id: number,
	image: string,
	preparationTime?: string,
	title: string,
}

export const useSearchStore = defineStore('search', () => {
	// state
	const visibleSearchPopup = ref(false);
	const searchValue = ref('');
	const searchResult = ref({});
	const searchLoading = ref(false);
	const searchGsoaId = ref<number>();
	const searchFetchedText = ref('');
	const searchFetched = ref(false);

	const products = ref<GsoaProductCatalogProductDetail[]>([]);
	const productsCurrentPage = ref(0);
	const productsPaginationShowMore = ref(false);
	const productsLoadingMore = ref(false);

	const blogs = ref<StoryblokSearchContent[]>();
	const recipes = ref<StoryblokSearchContent[]>();

	const oftenProducts = ref<GsoaProductCatalogProductDetail[]>([]);
	const luigisBoxClientId = useCookie<string>(LUIGIS_BOX_CLIENT_ID_COOKIE, {maxAge: 3600 * 24, decode: decodeURIComponent});
	const luigisBoxCookieClientId = useCookie<string | null>(LUIGIS_BOX_COOKIE, {decode: decodeURIComponent});

	// getters
	const getSearchValue = computed(() => searchValue.value);
	const getLoading = computed(() => searchLoading.value);

	const getProducts = computed(() => products.value);
	const getOftenProducts = computed(() => oftenProducts.value);
	const getProductsPaginationShowMore = computed(() => productsPaginationShowMore.value);
	const getProductsLoadingMore = computed(() => productsLoadingMore.value);
	const getBlogs = computed(() => blogs.value);
	const getRecipes = computed(() => recipes.value);

	const {marketingAllowed} = useCookieConsent();
	let activeSearchKey: string | null = null;
	let queuedSearchData: SearchData | null = null;

	// methods
	async function fetchSearch(data: SearchData) {
		if (!data.gsoaId || hasSearchData(data.text, data.gsoaId)) {
			return;
		}

		const searchKey = createSearchKey(data);
		if (searchLoading.value) {
			queuedSearchData = activeSearchKey === searchKey ? null : data;
			return;
		}

		queuedSearchData = data;
		set(searchLoading, true);

		try {
			while (queuedSearchData !== null) {
				const currentSearchData = queuedSearchData;
				queuedSearchData = null;
				activeSearchKey = createSearchKey(currentSearchData);
				await fetchSearchData(currentSearchData);
			}
		} finally {
			activeSearchKey = null;
			set(searchLoading, false);
		}
	}

	async function fetchSearchData(data: SearchData) {
		set(searchValue, data.text);
		set(searchGsoaId, data.gsoaId);
		set(searchFetched, false);
		set(productsCurrentPage, 0);
		set(productsPaginationShowMore, false);
		set(searchResult, {});
		set(oftenProducts, []);
		set(blogs, []);
		set(recipes, []);

		const [
			productsResponse,
			oftenProductsResponse,
			blogArticles,
			recipesArticles,
		] = await Promise.allSettled([
			GsoaRecommenderService.searchProductsCatalog({...getProductSearchParams(data.gsoaId, data.text, productsCurrentPage.value)}),
			GsoaRecommenderService.getCommonBuyProductsCatalog({
				houseNumber: data.gsoaId,
				pageSize: 5,
			}),
			cancelTimeout(() => StoryblokSearchService.getApiVStoryblokSearch({
				type: 'blog',
				q: data.text,
				offset: 0,
				limit: 5,
			})),
			cancelTimeout(() => StoryblokSearchService.getApiVStoryblokSearch({
				type: 'recipes',
				q: data.text,
				offset: 0,
				limit: 5,
			})),
		]);

		if (queuedSearchData !== null) {
			return;
		}

		if (productsResponse.status === 'fulfilled') {
			products.value = productsResponse.value?.products || [];
			productsPaginationShowMore.value = productsResponse.value?.paginationShowMore || false;
		}

		if (oftenProductsResponse.status === 'fulfilled') {
			oftenProducts.value = oftenProductsResponse.value?.products || [];
		}

		if (blogArticles.status === 'fulfilled') {
			blogs.value = blogArticles.value?.items || [];
		}

		if (recipesArticles.status === 'fulfilled') {
			recipes.value = recipesArticles.value?.items || [];
		}
		set(searchFetchedText, data.text);
		set(searchFetched, productsResponse.status === 'fulfilled');
	}

	function createSearchKey(data: SearchData) {
		return `${data.gsoaId}:${data.text}`;
	}

	function hasSearchData(text: string, gsoaId: number) {
		return searchFetched.value
			&& searchFetchedText.value === text
			&& searchGsoaId.value === gsoaId;
	}

	async function searchProductsCatalogOnly(text: string, gsoaId: number, page = 0) {
		if (!gsoaId || !text) {
			return {products: [] as GsoaProductCatalogProductDetail[], paginationShowMore: false};
		}

		const response = await GsoaRecommenderService.searchProductsCatalog(getProductSearchParams(gsoaId, text, page));

		return {
			products: response?.products ?? [],
			paginationShowMore: response?.paginationShowMore ?? false,
		};
	}

	async function loadMoreProducts(houseNumber: number) {
		set(productsLoadingMore, true);
		const params = getProductSearchParams(houseNumber, searchValue.value, productsCurrentPage.value + 1);

		try {
			const moreProductsResponse = await GsoaRecommenderService.searchProductsCatalog(params);

			products.value = products.value?.concat(moreProductsResponse?.products ?? []) ?? moreProductsResponse?.products;
			productsCurrentPage.value = productsCurrentPage.value + 1;
			set(productsPaginationShowMore, moreProductsResponse?.paginationShowMore ?? false);
		} catch (e: any) {
			throw createError({
				message: 'Failed to fetch more products',
				data: params,
				cause: e,
			});
		} finally {
			set(productsLoadingMore, false);
		}
	}

	async function trackSearchAnalytics(data: SearchAnalyticsData) {
		const pageUrl = useCurrentUrl() || undefined;
		const items = data.products.flatMap((product) => {
			if (!product.vanr) {
				return [];
			}

			return [{
				itemId: product.vanr,
				title: product.name || product.vanr,
			}];
		});

		try {
			await LuigisBoxService.uploadSearchAnalytics({
				requestBody: {
					query: data.text,
					clientId: getLuigisBoxClientId(),
					consentGranted: marketingAllowed.value,
					positionOffset: data.positionOffset || 0,
					...(pageUrl ? {pageUrl} : {}),
					items,
				},
			});
		} catch (error) {
			console.error('Failed to upload Luigi search analytics', error);
		}
	}

	function getProductSearchParams(houseNumber: number, searchText: string, page: number, autocomplete: boolean = false) {
		return {
			houseNumber,
			filter: searchText,
			clientId: getLuigisBoxClientId(),
			page,
			pageSize: 5,
			autocomplete,
		};
	}

	function getLuigisBoxClientId() {
		if (luigisBoxCookieClientId.value) {
			return luigisBoxCookieClientId.value;
		}

		if (!luigisBoxClientId.value) {
			luigisBoxClientId.value = uuidv4();
		}

		return luigisBoxClientId.value;
	}

	function setVisibleSearchPopup(state: boolean) {
		set(visibleSearchPopup, state);
	}

	function setSearchValue(value: string) {
		set(searchValue, value);
	}

	return {
		visibleSearchPopup,
		searchResult,
		searchLoading,
		getSearchValue,
		getLoading,
		getProducts,
		getOftenProducts,
		getProductsPaginationShowMore,
		getProductsLoadingMore,
		getBlogs,
		getRecipes,
		fetchSearch,
		hasSearchData,
		searchProductsCatalogOnly,
		loadMoreProducts,
		trackSearchAnalytics,
		setVisibleSearchPopup,
		setSearchValue,
	};
});
